import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, fmtNum } from "@/lib/api";
import { useMasters, opt } from "@/lib/useMasters";
import { useOnline } from "@/lib/useOnline";
import { PageHeader, Card, Field, SelectInput, TextInput, NumberInput, TextArea, SectionTitle, StatusBadge } from "@/components/kit";
import TxnRecent, { transferColumns, transferFields } from "@/components/TxnRecent";
import { Loader2, WifiOff, ArrowLeftRight } from "lucide-react";

export default function Transfer() {
  const m = useMasters();
  const online = useOnline();
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const { data: lots = [] } = useQuery({ queryKey: ["lots-active"], queryFn: async () => (await api.get("/reports/lots")).data });
  const { data: transfers = [] } = useQuery({ queryKey: ["transfers"], queryFn: async () => (await api.get("/txn/transfer")).data });
  const activeLots = lots.filter((l) => l.current_bags > 0);

  const [f, setF] = useState({ source_lot_id: "", dest_warehouse_id: "", dest_stack_id: "", bags: "", net_weight: "", reason: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const lot = useMemo(() => activeLots.find((l) => String(l.id) === String(f.source_lot_id)), [f.source_lot_id, activeLots]);

  const destStacks = opt(
    m.stacks.filter((s) => String(s.warehouse_id) === String(f.dest_warehouse_id) && String(s.id) !== String(lot?.stack_id)),
    (s) => {
      const occ = s.occupancy || {};
      const pct = occ.space_used_pct ?? occ.utilisation;
      const tag = occ.stack_complete ? " · COMPLETE" : "";
      return `${s.code} — ${pct != null ? pct + "% space" : ""}${tag}`;
    }
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!online) return toast.error("You are offline. Transfers require a live connection.");
    if (!f.source_lot_id || !f.dest_warehouse_id || !f.dest_stack_id || !f.bags) return toast.error("Fill source lot, destination and bags.");
    if (lot && +f.bags > lot.current_bags) return toast.error(`Only ${lot.current_bags} bags available.`);
    setSubmitting(true);
    try {
      const { data } = await api.post("/txn/transfer", {
        source_lot_id: +f.source_lot_id, dest_warehouse_id: +f.dest_warehouse_id, dest_stack_id: +f.dest_stack_id,
        bags: +f.bags, net_weight: +f.net_weight || 0, reason: f.reason,
      });
      toast.success(`${data.transfer_number}: ${data.source_lot} → ${data.dest_lot}${data.new_dest_lot ? " (new lot)" : ""}`);
      qc.invalidateQueries();
      setF({ source_lot_id: "", dest_warehouse_id: "", dest_stack_id: "", bags: "", net_weight: "", reason: "" });
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  if (m.loading) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-emerald-700" size={32} /></div>;

  return (
    <div data-testid="transfer-page">
      <PageHeader title="Stock Transfer" subtitle="Move bags between stacks / warehouses — destination lot traces back to source" />
      {!online && <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"><WifiOff size={16} /> Offline — transfers disabled.</div>}

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <SectionTitle>Transfer Details</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Source Lot" required className="md:col-span-2">
              <SelectInput options={activeLots.map((l) => ({ value: l.id, label: `${l.lot_number} · ${l.stock_owner_name} · ${l.item_name} · ${fmtNum(l.current_bags)} bags @ ${l.warehouse_name}/${l.stack_code}` }))}
                value={f.source_lot_id} onChange={set("source_lot_id")} data-testid="transfer-source-lot" />
            </Field>
            {lot && (
              <div className="md:col-span-2 grid grid-cols-3 gap-3">
                <Avail label="Available Bags" value={fmtNum(lot.current_bags)} />
                <Avail label="Available Weight" value={`${fmtNum(lot.current_weight)} Qtl`} />
                <Avail label="Current Location" value={`${lot.warehouse_name}/${lot.stack_code}`} />
              </div>
            )}
            <Field label="Destination Warehouse" required>
              <SelectInput options={opt(m.warehouses, (w) => w.name)} value={f.dest_warehouse_id} onChange={(e) => setF((s) => ({ ...s, dest_warehouse_id: e.target.value, dest_stack_id: "" }))} data-testid="transfer-dest-wh" />
            </Field>
            <Field label="Destination Stack" required>
              <SelectInput options={destStacks} value={f.dest_stack_id} onChange={set("dest_stack_id")} data-testid="transfer-dest-stack" placeholder={f.dest_warehouse_id ? "Select stack" : "Select warehouse first"} />
            </Field>
            <Field label="Bags" required><NumberInput value={f.bags} onChange={set("bags")} data-testid="transfer-bags" /></Field>
            <Field label="Net Weight (Qtl)"><NumberInput value={f.net_weight} onChange={set("net_weight")} data-testid="transfer-net" /></Field>
            <Field label="Reason" className="md:col-span-2"><TextArea rows={2} value={f.reason} onChange={set("reason")} placeholder="Reason for transfer" /></Field>
          </div>
          <button type="submit" disabled={submitting || !online} data-testid="transfer-submit"
            className="mt-4 flex h-11 items-center gap-2 rounded-full bg-emerald-800 px-6 font-semibold text-white hover:bg-emerald-900 active:scale-[0.99] disabled:opacity-60">
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <ArrowLeftRight size={18} />} Post Transfer
          </button>
        </Card>

        <Card className="p-5">
          <SectionTitle>Recent Transfers</SectionTitle>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {transfers.length === 0 && <div className="text-sm text-stone-400">No transfers yet.</div>}
            {transfers.map((t) => (
              <div key={t.id} className="rounded-lg border border-stone-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-emerald-700">{t.transfer_number}</span>
                  <StatusBadge tone="emerald">{fmtNum(t.bags)} bags</StatusBadge>
                </div>
                <div className="mt-1 text-xs text-stone-500">{t.source_lot_number} → {t.dest_lot_number}</div>
                <div className="text-xs text-stone-400">{t.owner_name} · {t.item_name} · {t.date}</div>
              </div>
            ))}
          </div>
        </Card>
      </form>
      <TxnRecent kind="transfer" title="All Transfers" columns={transferColumns} editFields={transferFields} />
    </div>
  );
}

const Avail = ({ label, value }) => (
  <div className="rounded-lg bg-stone-50 p-3"><div className="text-[11px] text-stone-500">{label}</div><div className="font-semibold text-stone-900 text-sm">{value}</div></div>
);
